import React, { useEffect } from 'react';
import { EmailDataFormParameters } from 'store/auth/actions/auth-actions/auth-action-types';
import { authSendSsoEmail } from 'store/auth/actions/auth-sso/auth-sso';
import { ssoSplunkActions } from 'features/authentication/sso/constants';
import { useQuery, useRouteParam } from 'hooks/use-query/use-query';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { ssoBaseSplunkData } from 'features/authentication/utils/constants';
import { isSsoLoginSourcePage } from 'features/authentication/utils/sso/sso';
import { useSegmentCallback } from 'hooks/use-segment-callback/use-segment-callback';
import { heapIdentify, heapTrack } from 'utils/heap/heap';
import { SsoPage } from 'features/authentication/sso/components/sso-page/sso-page';
import { AuthPageWrapper } from 'features/authentication/auth-page-wrapper/auth-page-wrapper';

export const Sso = () => {
    const next_url = useRouteParam('next_url');
    const ssoEmail = useQuery('sso_email')!;
    const isForceSso = useQuery('required_sso') === 'true';
    const sendEvent = useSplunkCallback(ssoBaseSplunkData);
    const loginSourcePage = isSsoLoginSourcePage();
    const { track } = useSegmentCallback();

    const onEmailFormSubmit = () => {
        const action = isForceSso
            ? ssoSplunkActions.forceSsoSuccess
            : loginSourcePage
            ? ssoSplunkActions.loginWithSsoSuccess
            : ssoSplunkActions.signupWithSsoSuccess;

        sendEvent({ action });

        if (loginSourcePage) {
            track({
                category: 'User',
                action: 'Signed In',
            });
        } else if (!loginSourcePage) {
            track({
                category: 'User',
                action: 'Signed Up',
            });
        }
    };

    useEffect(() => {
        let action: string;

        if (isForceSso) {
            action = ssoSplunkActions.forceSsoViewed;
        } else {
            action = loginSourcePage
                ? ssoSplunkActions.loginWithSsoPageViewed
                : ssoSplunkActions.signupWithSsoPageViewed;
        }

        sendEvent({ action });
    }, [isForceSso, sendEvent, loginSourcePage, track]);

    const onSendEmail = (data: EmailDataFormParameters) => {
        sendEvent({
            action: loginSourcePage
                ? ssoSplunkActions.loginWithSsoLoginClick
                : ssoSplunkActions.signupWithSsoSignupClick,
        });

        heapIdentify(data.email);

        if (loginSourcePage) {
            track({
                category: 'User',
                action: 'Signed In',
                emailInput: data.email,
            });
        } else {
            track({
                category: 'User',
                action: 'Signed Up',
                emailInput: data.email,
            });
            heapTrack('Account Activated', {
                method: 'SSO',
            });
            heapTrack('Signup Success', {
                method: 'sso',
            });
        }
        return authSendSsoEmail(data, next_url);
    };

    return (
        <AuthPageWrapper>
            <SsoPage
                onSendEmail={onSendEmail}
                onSuccessCallback={onEmailFormSubmit}
                isLoginMode={loginSourcePage}
                email={ssoEmail}
            />
        </AuthPageWrapper>
    );
};
